// PartitaGruppo.tsx — la partita a 4 (v013): griglia 2×2 con i video di tutti, sala d'attesa (pronti, il via di chi
// organizza), conto alla rovescia, «Ha riso!» sul video di chi ride, accuse, pause, eliminati che guardano, classifica.
// Mostra solo quello che decide il server (la "vista"); le azioni diventano messaggi al server.
import { InvitaAmici } from "../componenti/InvitaAmici.tsx";
import { vibra as tocco } from "../servizi/nativo.ts";
import { IconaEsci, IconaRisata, IconaSegnala, IconaAltoparlante } from "../componenti/IconeGioco.tsx";
import { Tutorial, tutorialVisto } from "../componenti/Tutorial.tsx";
import { PulsanteBot } from "../prova/PulsanteBot.tsx";
import { conVoce, useVoce } from "../gioco/usaVoce.ts";
import { IconaVoce, SceltaVoce } from "../gioco/SceltaVoce.tsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Finestra } from "../componenti/Finestra.tsx";
import { Condividi, indirizzoSito } from "../gioco/Condividi.tsx";
import { FinestraFinePartita } from "../gioco/FinePartita.tsx";
import { Annuncio } from "../gioco/AnnuncioRisultato.tsx";
import { DomandaScelte } from "../componenti/DomandaScelte.tsx";
import { domandaDaFare } from "../servizi/scelte.ts";
import { clipPossibili } from "../gioco/usaClip.ts";
import { esitoGruppo, useClipGruppo, useFotoGruppo } from "./clipGruppo.ts";
import { leggiRegoleStelle } from "../servizi/social.ts";
import { SceltaFiltro } from "../gioco/SceltaFiltro.tsx";
import { useOraServer } from "../gioco/Partita.tsx";
import { stanzaGruppo, useStanzaGruppo } from "../gioco/stanzaGruppo.ts";
import { Maglia } from "../gioco/maglia.ts";
import type { StatoVideo } from "../gioco/video.ts";
import { avviaAnalisi, preparaRilevamento, type Analisi } from "../gioco/rilevamento.ts";
import { useFiltri } from "../gioco/usaFiltri.ts";
import { coriandoli, sbloccaAudio, suono, suoniAttivi, vibra } from "../gioco/festa.ts";
import { tempoReale } from "../servizi/tempoReale.ts";
import { aggiungiBotGruppo, linkGruppo, segnalaGruppo, serverIceGruppo } from "../servizi/gruppi.ts";
import { Segnala } from "../gioco/Segnala.tsx";
import type { ServerIce } from "../servizi/sfide.ts";
import { useAccount } from "../servizi/account.ts";
import { opzioneProva } from "../servizi/prova.ts";
import { eNumero, leggiImpostazione } from "../servizi/impostazioni.ts";
import { salvaPreferenza } from "../servizi/preferenze.ts";
import { IMP_APP, PREFERENZE } from "../costanti.ts";
import { POSTI_GRUPPO, numeroPosto, type Posto, type VistaGruppo } from "../../../condiviso/gruppo.ts";
import { creaVideoBot, type VideoBot } from "../prova/videoBot.ts";
import { PannelloProvaGruppo } from "../prova/PannelloProva.tsx";

const secondi = (ms: number) => Math.max(0, Math.ceil(ms / 1000));
const mmss = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
const inGara = (v: VistaGruppo, p: Posto) => v.inGara[p] && v.fuori[p] === null;

export function PartitaGruppo({ codice, flusso, onEsci }: { codice: string; flusso: MediaStream; onEsci: () => void }) {
  const { t, i18n } = useTranslation();
  const st = useStanzaGruppo();
  const v = st.vista;
  const io: Posto = st.posto ?? "p1";
  const fase = v?.fase ?? "attesa";
  // v030: mini tutorial della partita a 4 la prima volta
  const [tutorial, setTutorial] = useState(false);
  useEffect(() => {
    if (v && fase === "attesa" && !tutorialVisto("gruppo")) setTutorial(true);
  }, [v, fase]);
  const ora = useOraServer(["conto", "gioco", "accusa", "pausa", "verifica"].includes(fase));
  // v035: si è «pronti» da soli (il «Via» lo dà sempre chi organizza); chi non è pronto preme «Aspetta un attimo»
  const [aspetta, setAspetta] = useState(false);
  useEffect(() => {
    if (!v || fase !== "attesa" || aspetta || tutorial || v.pronti[io]) return;
    const id = setTimeout(() => {
      sbloccaAudio();
      stanzaGruppo.pronto(true);
    }, 1200);
    return () => clearTimeout(id);
  }, [v, fase, aspetta, tutorial, io]);
  const [conferma, setConferma] = useState(false);
  /** Segnala (v015): "scegli" = elenco dei giocatori; poi il posto scelto. */
  const [segnala, setSegnala] = useState<"scegli" | Posto | null>(null);
  const bloccatoRif = useRef(false);
  const [sceltaFiltro, setSceltaFiltro] = useState(false);
  const [sceltaVoce, setSceltaVoce] = useState(false);
  const [suoni, setSuoni] = useState(suoniAttivi);
  const [remoti, setRemoti] = useState<Partial<Record<Posto, MediaStream | null>>>({});
  const [statiVideo, setStatiVideo] = useState<Partial<Record<Posto, StatoVideo>>>({});
  const [rilevamento, setRilevamento] = useState<"caricamento" | "attivo" | "assente">("caricamento");
  const [voltoMio, setVoltoMio] = useState(true);
  const filtri = useFiltri(flusso);
  const video = useRef<Partial<Record<Posto, HTMLVideoElement | null>>>({});
  const tela = useRef<HTMLCanvasElement>(null);
  const faseRif = useRef(fase);
  faseRif.current = fase;

  const account = useAccount();
  const tester = account.tipo === "registrato" && account.io.tester === true;
  const [botInCorso, setBotInCorso] = useState(false);
  const soloTurn = tester && opzioneProva(PREFERENZE.provaSoloTurn);

  // --- il mio video (con il filtro, se acceso) -----------------------------------------------------------
  const invio = filtri.motore && filtri.uscitaAttiva ? filtri.motore.uscita : flusso;
  const invioRif = useRef(invio);
  invioRif.current = invio;
  const magliaRif = useRef<Maglia | null>(null);
  // v026: voce buffa
  const voce = useVoce(flusso);
  // v038b: con l'audio spento il mio microfono non manda niente (e all'entrata si ritrova com'era l'ultima volta)
  const mioMuto = v?.muti?.[io] === true;
  useEffect(() => {
    for (const tr of flusso.getAudioTracks()) tr.enabled = !mioMuto;
  }, [flusso, mioMuto]);
  const mutoMandato = useRef(false);
  useEffect(() => {
    if (!v || mutoMandato.current) return;
    mutoMandato.current = true;
    if (!suoni && !v.muti?.[io]) stanzaGruppo.muto(true);
  }, [v, io, suoni]);
  const voceRif = useRef(voce.traccia);
  voceRif.current = voce.traccia;
  useEffect(() => {
    if (voce.traccia) magliaRif.current?.sostituisciAudio(voce.traccia);
  }, [voce.traccia]);
  useEffect(() => {
    const el = video.current[io];
    if (el) el.srcObject = invio;
    const traccia = invio.getVideoTracks()[0];
    if (traccia) magliaRif.current?.sostituisciVideo(traccia);
  }, [invio, io]);

  // --- video a maglia con gli altri ----------------------------------------------------------------------
  // Con chi vedersi: gli altri collegati; la coppia si rifà quando uno dei due si ricollega (contatore delle sessioni).
  const voluti = useMemo(() => {
    const r: Partial<Record<Posto, string>> = {};
    if (!v) return r;
    // v039: con i bot (partita di prova) niente videochiamata: il loro video è disegnato qui
    for (const p of POSTI_GRUPPO) if (p !== io && v.collegati[p] && !v.bot?.[p]) r[p] = `${v.sessioni[io]}-${v.sessioni[p]}`;
    return r;
  }, [v, io]);
  const chiaveVoluti = JSON.stringify(voluti);
  const volutiRif = useRef(voluti);
  volutiRif.current = voluti;

  useEffect(() => {
    if (st.passo !== "dentro" || !st.token || !st.posto) return;
    const token = st.token;
    // Server video (STUN/TURN con credenziali a tempo): chiesti una volta e rinnovati dopo un po'; se la richiesta
    // non riesce la videochiamata aspetta e riprova (senza server, dietro molte reti non si vedrebbe nulla).
    let ice: { p: Promise<ServerIce[]>; il: number } | null = null;
    const rinnovoMs = Math.max(1, leggiImpostazione(IMP_APP.gruppiIceRinnovoMinuti, 30, eNumero)) * 60_000;
    const m = new Maglia({
      io: st.posto,
      soloTurn,
      distanzaMs: leggiImpostazione(IMP_APP.gruppiVideoDistanzaMs, 350, eNumero),
      ice: () => {
        if (!ice || Date.now() - ice.il > rinnovoMs) {
          const p = serverIceGruppo(codice, token).then((r) => r.iceServers);
          ice = { p, il: Date.now() };
          p.catch(() => {
            if (ice?.p === p) ice = null; // la prossima volta si richiede
          });
        }
        return ice.p;
      },
      locale: () => conVoce(invioRif.current, voceRif.current),
      invia: (con, d, sessione) => stanzaGruppo.segnale(con, d, sessione),
      suRemoto: (p, s) => setRemoti((r) => ({ ...r, [p]: s })),
      suStato: (p, s) => setStatiVideo((x) => ({ ...x, [p]: s })),
    });
    magliaRif.current = m;
    const stacca = stanzaGruppo.suSegnale((da, d, sessione) => m.ricevi(da, d, sessione));
    m.aggiorna(volutiRif.current);
    return () => {
      stacca();
      m.chiudi();
      magliaRif.current = null;
      setRemoti({});
    };
  }, [st.passo, st.token, st.posto, codice, soloTurn]);
  useEffect(() => magliaRif.current?.aggiorna(volutiRif.current), [chiaveVoluti]);

  // v039: il "video" dei bot (faccina disegnata che ride quando il server dice che ha riso)
  const postiBot = v?.bot ? POSTI_GRUPPO.filter((p) => v.bot?.[p]) : [];
  const chiaveBot = postiBot.join(",");
  const videoBot = useRef<Partial<Record<Posto, VideoBot>>>({});
  useEffect(() => {
    if (!chiaveBot) return;
    const stile = getComputedStyle(document.documentElement);
    const colore = (n: string, d: string) => stile.getPropertyValue(n).trim() || d;
    const creati: Partial<Record<Posto, VideoBot>> = {};
    for (const p of chiaveBot.split(",") as Posto[]) creati[p] = creaVideoBot({ sfondo: colore("--c-superficie", "#fff6e0"), faccia: colore("--c-accento", "#ffd23f"), tratto: colore("--c-testo", "#1e1433") });
    videoBot.current = creati;
    setRemoti((r) => ({ ...r, ...Object.fromEntries(Object.entries(creati).map(([p, b]) => [p, b!.flusso])) }));
    return () => {
      for (const b of Object.values(creati)) b?.ferma();
      videoBot.current = {};
    };
  }, [chiaveBot]);
  useEffect(() => {
    for (const [p, b] of Object.entries(videoBot.current) as [Posto, VideoBot][]) {
      const fuori = v?.fuori[p];
      b.imposta(v?.verifica?.chi.includes(p) || (fuori && fuori.motivo === "risata") ? "ride" : "neutro");
    }
  }, [v]);

  useEffect(() => {
    for (const p of POSTI_GRUPPO) {
      if (p === io) continue;
      const el = video.current[p];
      const s = remoti[p] ?? null;
      if (el && el.srcObject !== s) el.srcObject = s;
    }
  }, [remoti, io]);

  // --- v042: video delle risate e foto della partita (come nell'1 contro 1), e la pagina per condividerli ---------
  const linkMio = account.tipo === "registrato" && account.io.nomeUtente && account.io.linkAttivo ? `${indirizzoSito()}/@${account.io.nomeUtente}` : indirizzoSito();
  const clip = useClipGruppo({ vista: v, io, invio, remoti, lingua: i18n.language, link: new URL(linkMio).host + new URL(linkMio).pathname.replace(/\/$/, "") });
  const { foto: fotoGruppo, conAltri: fotoConAltri } = useFotoGruppo({ vista: v, io, camera: flusso, video });
  const [fineAperta, setFineAperta] = useState(false);
  // v045: dopo la primissima partita «Le tue scelte» (o il promemoria, una volta per apertura), poi la fine
  const [domanda, setDomanda] = useState<"prima" | "promemoria" | null>(null);
  const fineVista = useRef<number | null>(null);
  useEffect(() => {
    if (fase !== "risultato" || !v?.classifica) {
      setFineAperta(false);
      setDomanda(null);
      return;
    }
    if (fineVista.current === v.round) return;
    fineVista.current = v.round;
    const id = setTimeout(() => {
      const d = domandaDaFare(clipPossibili());
      if (d) setDomanda(d);
      else setFineAperta(true);
    }, 1600);
    return () => clearTimeout(id);
  }, [fase, v?.round, v?.classifica]);
  const [premi, setPremi] = useState<{ condivisione: number; amico: number } | null>(null);
  const registratoQui = account.tipo === "registrato";
  useEffect(() => {
    if (!registratoQui) return;
    void leggiRegoleStelle()
      .then((r) => setPremi(r.attivi ? { condivisione: r.condivisione ?? 0, amico: r.amicoNuovo } : null))
      .catch(() => undefined);
  }, [registratoQui]);
  const altriNomi = v ? POSTI_GRUPPO.filter((x) => x !== io && v.occupati[x] && v.nomi[x]).map((x) => v.nomi[x]) : [];
  const nomeAltri = altriNomi.length ? new Intl.ListFormat(i18n.language, { type: "conjunction" }).format(altriNomi) : t("gioco.avversario");
  const esito = v && fase === "risultato" ? esitoGruppo(v) : null;
  const vintoIo = esito ? (esito.vinto === io ? true : esito.vinto === null && v?.classifica?.some((c) => c.posto === io && c.posizione === 1) ? null : false) : null;

  // --- rilevamento: il mio volto e quello del vicino che mi tocca guardare --------------------------------------
  const vicino = v && fase !== "attesa" && fase !== "risultato" ? v.guardi[io] : null;
  const analisi = useRef<Analisi | null>(null);
  const motore = filtri.motore;
  const motoreDeciso = filtri.stato !== "attesa";
  useEffect(() => {
    if (!motoreDeciso || !vicino) return;
    let fermo = false;
    void preparaRilevamento().then((fl) => {
      if (fermo) return;
      const elIo = video.current[io];
      const elLui = video.current[vicino];
      if (!fl || !elIo || !elLui) {
        setRilevamento("assente");
        return;
      }
      setRilevamento("attivo");
      // L'analisi riparte supponendo il volto presente: lo si dice anche al server (se prima mancava, lì resterebbe «assente»).
      setVoltoMio(true);
      stanzaGruppo.volto(true);
      analisi.current = avviaAnalisi(fl, { io: motore ?? elIo, avversario: elLui }, () => tempoReale.oraServer(), (chi, e) => {
        if (e.tipo === "volto") {
          if (chi === "io") {
            setVoltoMio(e.presente);
            stanzaGruppo.volto(e.presente);
          }
          return;
        }
        if (faseRif.current === "gioco" || faseRif.current === "verifica") stanzaGruppo.risata(chi === "io" ? io : vicino, e.t);
      });
    });
    return () => {
      fermo = true;
      analisi.current?.ferma();
      analisi.current = null;
    };
  }, [vicino, io, motore, motoreDeciso]);
  useEffect(() => {
    if (fase === "conto") analisi.current?.azzera();
  }, [fase, v?.tVia]);

  // --- suoni, festa ---------------------------------------------------------------------------------------
  const numeroConto = fase === "conto" && v?.tVia ? secondi(v.tVia - ora) : 0;
  const ultimoNumero = useRef(0);
  useEffect(() => {
    if (fase === "conto" && numeroConto > 0 && numeroConto !== ultimoNumero.current) suono("conto");
    if (fase === "gioco" && ultimoNumero.current > 0) {
      suono("via");
      vibra("via");
    }
    ultimoNumero.current = fase === "conto" ? numeroConto : 0;
  }, [fase, numeroConto]);

  const mioFuori = v?.fuori[io] ?? null;
  const fuoriPrima = useRef<string | null>(null);
  useEffect(() => {
    const k = mioFuori ? `${v?.round}-${mioFuori.posizione}` : null;
    if (k && fuoriPrima.current !== k && fase !== "risultato") {
      suono("sconfitta");
      vibra("sconfitta");
    }
    fuoriPrima.current = k;
  }, [mioFuori, v?.round, fase]);

  const miaPosizione = v?.classifica?.find((c) => c.posto === io)?.posizione ?? null;
  const festeggiato = useRef<number | null>(null);
  const fermaCoriandoli = useRef<(() => void) | null>(null);
  useEffect(() => () => fermaCoriandoli.current?.(), []);
  useEffect(() => {
    if (fase !== "risultato" || !v || festeggiato.current === v.round) return;
    festeggiato.current = v.round;
    if (miaPosizione === 1) {
      suono("vittoria");
      vibra("vittoria");
      const stile = getComputedStyle(document.documentElement);
      const colori = ["--c-primario", "--c-accento", "--c-secondario", "--c-successo", "--c-sfondo"].map((c) => stile.getPropertyValue(c).trim()).filter(Boolean);
      fermaCoriandoli.current?.();
      if (tela.current) fermaCoriandoli.current = coriandoli(tela.current, colori, leggiImpostazione(IMP_APP.festaSecondi, 4, eNumero) * 1000);
    }
  }, [fase, v, miaPosizione]);

  // --- riquadri: io per primo, poi gli altri in ordine di posto -----------------------------------------------
  const ordine = [io, ...POSTI_GRUPPO.filter((p) => p !== io)];
  const tempoGiocato = v ? v.tempoGiocatoMs + (v.orologioDa !== null ? Math.max(0, ora - v.orologioDa) : 0) : 0;
  const possoAccusare = !!v && fase === "gioco" && inGara(v, io);

  return (
    <div className={`partita partita-gruppo fase-${fase}`} data-fase={fase}>
      <div className="campo-gioco griglia-gruppo">
        {ordine.map((p) => {
          const occupato = v?.occupati[p] ?? false;
          const nome = p === io ? t("gioco.tu") : v?.nomi[p] || "";
          const fuori = v?.fuori[p] ?? null;
          const posizione = v?.classifica?.find((c) => c.posto === p)?.posizione ?? null;
          const ride = v?.verifica?.chi.includes(p) ?? false;
          let avviso: string | null = null;
          if (!occupato) avviso = t("gruppo.posto_libero");
          else if (p !== io && !v?.collegati[p]) avviso = t("gruppo.non_collegato");
          else if (p !== io && statiVideo[p] && statiVideo[p] !== "collegato") avviso = t(statiVideo[p] === "interrotto" ? "gioco.video_interrotto" : "gioco.video_collegamento");
          else if (p === io && !voltoMio && ["attesa", "conto", "gioco"].includes(fase)) avviso = t("gioco.inquadra");
          else if (fase === "accusa" && v?.accusa?.contro === p) avviso = t("gruppo.accusato");
          const esito = fase === "risultato" && posizione === 1 ? "ha_vinto" : fuori || ride ? "ha_riso" : null;
          return (
            <figure key={p} className={`riquadro riquadro-gruppo${p === io ? " riquadro-io" : ""}`} data-posto={p} data-esito={esito ?? undefined} data-vuoto={occupato ? undefined : "1"}>
              <video
                ref={(el) => {
                  video.current[p] = el;
                }}
                autoPlay
                playsInline
                muted={p === io || mioMuto || v?.muti?.[p] === true}
                className={`video${p === io ? " video-specchio" : ""}`}
              />
              {occupato && v?.muti?.[p] && (
                <span className="ld-muto-badge" role="img" aria-label={p === io ? t("gioco.muto_io") : t("gioco.muto_lui", { nome })}>
                  <IconaAltoparlante acceso={false} dimensione={18} />
                </span>
              )}
              {occupato && (
                <figcaption className="riquadro-nome">
                  {nome}
                  {p === "p1" && (
                    <span className="riquadro-capo" role="img" aria-label={t("gruppo.organizza_etichetta")} title={t("gruppo.organizza_etichetta")}>
                      {" ★"}
                    </span>
                  )}
                </figcaption>
              )}
              {/* v041: il tester che organizza mette un bot nel posto vuoto (prima del primo via) */}
              {!occupato && p !== io && tester && io === "p1" && fase === "attesa" && st.token && (
                <span className="ld-posto-bot-angolo">
                  <PulsanteBot
                    tipo="posto"
                    disabled={botInCorso}
                    onClick={() => {
                      setBotInCorso(true);
                      void aggiungiBotGruppo(codice, st.token!).catch(() => undefined).finally(() => setBotInCorso(false));
                    }}
                  />
                </span>
              )}
              {/* v019: con la posizione o «fuori» sopra il video l'avviso non serve (si accavallavano) */}
              {avviso && !(fase === "risultato" && posizione !== null) && !fuori && !ride && <span className="riquadro-avviso">{avviso}</span>}
              {fase === "risultato" && posizione !== null ? (
                <span className={`riquadro-esito esito-${posizione === 1 ? "ha_vinto" : "ha_riso"}`}>{t("gruppo.posizione", { n: posizione })}</span>
              ) : fuori ? (
                <span className="riquadro-esito esito-ha_riso">{t("gruppo.fuori", { n: fuori.posizione })}</span>
              ) : ride ? (
                <span className="riquadro-esito esito-ha_riso">{t("gioco.ha_riso")}</span>
              ) : null}
              {possoAccusare && p !== io && v && inGara(v, p) && (
                <button type="button" className="pulsante-risata" aria-label={t("gioco.ha_riso_pulsante")} title={t("gioco.ha_riso_pulsante")} onClick={() => {
                  tocco("medio");
                  stanzaGruppo.haRiso(p);
                }}>
                  <IconaRisata />
                </button>
              )}
              {p === io && filtri.stato === "pronto" && (
                <button type="button" className="pulsante-icona pulsante-filtro" aria-label={t("gioco.filtro")} aria-pressed={filtri.attivo !== null} onClick={() => setSceltaFiltro(true)}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 9c0-3 3.5-5 8-5s8 2 8 5v2c0 5-3.6 9-8 9s-8-4-8-9z" />
                    <circle cx="9" cy="10.5" r="1.4" fill="currentColor" />
                    <circle cx="15" cy="10.5" r="1.4" fill="currentColor" />
                    <path d="M9 15c1.8 1.4 4.2 1.4 6 0" />
                  </svg>
                </button>
              )}
              {/* v028: sul video degli altri, «segnala» in alto a destra (il «Ha riso!» è in basso) */}
              {p !== io && occupato && v && !v.bot?.[p] && (
                <button type="button" className="pulsante-icona pulsante-segnala" aria-label={t("gioco.segnala")} onClick={() => setSegnala(p)}>
                  <IconaSegnala />
                </button>
              )}
              {p === io && voce.disponibile && (
                <button type="button" className="pulsante-icona pulsante-voce" aria-label={t("voce.titolo")} aria-pressed={voce.voce !== "normale"} onClick={() => setSceltaVoce(true)}>
                  <IconaVoce />
                </button>
              )}
            </figure>
          );
        })}
      </div>

      <canvas ref={tela} className="coriandoli" aria-hidden="true" />
      {/* v039: partita di prova con i bot (tester): i pulsanti per farli ridere, ridere io, vincere */}
      {v?.bot && postiBot.length > 0 && <PannelloProvaGruppo bot={postiBot} nomi={v.nomi} />}

      <header className="partita-testa">
        <button type="button" className="pulsante-icona pulsante-esci" aria-label={t("gioco.esci")} onClick={() => setConferma(true)}>
          <IconaEsci />
        </button>
        <div className="partita-punteggio" aria-live="polite">
          <span>{t("gruppo.titolo_breve")}</span>
          {v && v.round > 0 && <span>{t("gioco.round", { numero: v.round })}</span>}
          {fase === "gioco" && <span className="partita-tempo">{mmss(tempoGiocato)}</span>}
        </div>
        {/* v038b: spegnendo l'audio non senti nessuno e nessuno sente te; gli altri vedono l'icona sul tuo video */}
        <button
          type="button"
          className="pulsante-icona pulsante-audio"
          aria-label={mioMuto ? t("gioco.audio_accendi") : t("gioco.audio_spegni")}
          aria-pressed={mioMuto}
          onClick={() => {
            const muto = !mioMuto;
            setSuoni(!muto);
            salvaPreferenza(PREFERENZE.suoni, muto ? "no" : "si");
            stanzaGruppo.muto(muto);
          }}
        >
          <IconaAltoparlante acceso={!mioMuto} />
        </button>
      </header>

      <section className="partita-pannello" aria-live="polite">
        <Pannello v={v} io={io} ora={ora} numeroConto={numeroConto} codice={codice} rilevamentoAssente={rilevamento === "assente"} aspetta={aspetta} setAspetta={setAspetta} onApriFine={() => setFineAperta(true)} />
      </section>

      {/* v042: a fine partita le stesse scelte dell'1 contro 1: il video (con i quattro riquadri), le foto, «Fatti sfidare» */}
      {v?.classifica && fase === "risultato" && (
        <FinestraFinePartita
          aperta={fineAperta}
          onChiudi={() => setFineAperta(false)}
          vinto={vintoIo}
          nomeLui={nomeAltri}
          clip={clip}
          foto={fotoGruppo}
          fotoConLui={fotoConAltri}
          link={linkMio}
          premi={premi}
        />
      )}
      {domanda && fase === "risultato" && (
        <DomandaScelte
          modo={domanda}
          videoPossibile={clip.disponibile}
          onFine={() => {
            setDomanda(null);
            setFineAperta(true);
          }}
        />
      )}


      <Finestra aperta={sceltaVoce} titolo={t("voce.titolo")} onChiudi={() => setSceltaVoce(false)}>
        {sceltaVoce && (
          <SceltaVoce
            attiva={voce.voce}
            onScegli={(v) => {
              voce.scegli(v);
              setSceltaVoce(false);
            }}
          />
        )}
      </Finestra>
      <Finestra aperta={sceltaFiltro} titolo={t("gioco.filtro_titolo")} onChiudi={() => setSceltaFiltro(false)}>
        {sceltaFiltro && (
          <SceltaFiltro
            attivo={filtri.attivo}
            onScegli={(id) => {
              filtri.scegli(id);
              setSceltaFiltro(false);
            }}
          />
        )}
      </Finestra>

      <Finestra
        aperta={segnala !== null}
        titolo={segnala && segnala !== "scegli" ? t("gioco.segnala_titolo", { nome: v?.nomi[segnala] ?? "" }) : t("gruppo.segnala_chi")}
        // Chiusa con la X dopo aver bloccato qualcuno: si esce lo stesso.
        onChiudi={() => (bloccatoRif.current ? onEsci() : setSegnala(null))}
      >
        {segnala === "scegli" && v && (
          <div className="azioni-colonna">
            {POSTI_GRUPPO.filter((p) => p !== io && v.occupati[p]).map((p) => (
              <button key={p} type="button" className="pulsante pulsante-secondario" onClick={() => setSegnala(p)}>
                {v.nomi[p]}
              </button>
            ))}
          </div>
        )}
        {tutorial && <Tutorial tipo="gruppo" onFine={() => setTutorial(false)} />}
        {segnala && segnala !== "scegli" && st.token && (
          <Segnala
            nome={v?.nomi[segnala] ?? ""}
            codice={codice}
            token={st.token}
            invia={(motivo, nota) =>
              segnalaGruppo(codice, st.token!, numeroPosto(segnala), motivo, nota).then(() => {
                bloccatoRif.current = true; // v039: chi segnala blocca sempre
              })
            }
            onAnnulla={() => setSegnala(null)}
            // Bloccato: non si resta in videochiamata con chi si è bloccato.
            // v028: dopo la segnalazione si esce dalla partita
            onFatto={() => onEsci()}
          />
        )}
      </Finestra>

      <Finestra aperta={conferma} titolo={t("gioco.esci")} onChiudi={() => setConferma(false)}>
        <p>{t("gruppo.esci_conferma")}</p>
        <div className="azioni-riga">
          <button type="button" className="pulsante pulsante-secondario" onClick={() => setConferma(false)}>
            {t("generale.annulla")}
          </button>
          <button type="button" className="pulsante pulsante-pericolo" onClick={onEsci}>
            {t("gioco.esci")}
          </button>
        </div>
      </Finestra>
    </div>
  );
}

/** La parte in basso: cambia con la fase. */
function Pannello({ v, io, ora, numeroConto, codice, rilevamentoAssente, aspetta, setAspetta, onApriFine }: { v: VistaGruppo | null; io: Posto; ora: number; numeroConto: number; codice: string; rilevamentoAssente: boolean; aspetta: boolean; setAspetta: (a: boolean) => void; onApriFine: () => void }) {
  const { t } = useTranslation();
  if (!v) return <p className="pannello-testo">{t("generale.caricamento")}</p>;
  const nome = (p: Posto) => (p === io ? t("gioco.tu") : v.nomi[p] || "?");

  switch (v.fase) {
    case "attesa":
    case "risultato": {
      const collegati = POSTI_GRUPPO.filter((p) => v.collegati[p]);
      const tuttiPronti = collegati.every((p) => v.pronti[p]);
      const abbastanza = collegati.length >= v.minimo;
      const occupati = POSTI_GRUPPO.filter((p) => v.occupati[p]).length;
      return (
        <div className="pannello-pronti">
          {v.fase === "risultato" && v.classifica && (
            <>
              <p className={`pannello-titolo risultato-${v.classifica.find((c) => c.posto === io)?.posizione === 1 ? "vinto" : "perso"}`}>
                {v.classifica.filter((c) => c.posizione === 1).length > 1
                  ? t("gruppo.pari_primi")
                  : v.classifica[0]?.posto === io
                    ? t("gioco.hai_vinto")
                    : t("gruppo.ha_vinto", { nome: nome(v.classifica[0]!.posto) })}
              </p>
              <ol className="classifica-gruppo">
                {v.classifica.map((c) => (
                  <li key={c.posto} data-posizione={c.posizione}>
                    <span className="classifica-numero">{t("gruppo.posizione", { n: c.posizione })}</span> {nome(c.posto)}
                    {c.motivo && c.motivo !== "risata" && <span className="nota"> · {t(`gruppo.motivo_${c.motivo}`)}</span>}
                  </li>
                ))}
              </ol>
              {/* v042: «Condividi video e foto» per tutti (prima solo chi vinceva, con un messaggio di testo) */}
              <button type="button" className="ld-cta ld-cta-centrata ld-condividi-fine" onClick={onApriFine}>
                <svg viewBox="0 0 24 24" aria-hidden="true" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <path d="M8.6 10.6l6.8-4M8.6 13.4l6.8 4" />
                </svg>
                {t("fine.condividi_tutto")}
              </button>
            </>
          )}
          {v.fase === "attesa" && occupati < 4 && (
            <>
              <p className="pannello-titolo">{t("gruppo.in_attesa", { n: occupati })}</p>
              <Condividi link={linkGruppo(codice)} messaggio={t("gruppo.messaggio_condividi")} />
              <InvitaAmici tipo="gruppo" codice={codice} />
              {/* v045: mentre si aspetta, la pubblicità per chi è in Base (conta il piano di chi guarda) */}
              <Annuncio posto="attesa" />
            </>
          )}
          {rilevamentoAssente && <p className="nota">{t("gioco.rilevamento_assente")}</p>}
          <ul className="pronti-gruppo">
            {collegati.map((p) => (
              <li key={p} data-pronto={v.pronti[p] ? "1" : "0"}>
                {nome(p)} · {v.pronti[p] ? t("gruppo.pronto_si") : t("gruppo.pronto_no")}
              </li>
            ))}
          </ul>
          {v.fase === "attesa" ? (
            // v035: pronti da soli; si può solo chiedere di aspettare (e poi tornare pronti)
            <button
              type="button"
              className={`pulsante pulsante-grande ${aspetta ? "pulsante-primario" : "pulsante-secondario"}`}
              onClick={() => {
                sbloccaAudio();
                if (aspetta) setAspetta(false);
                else {
                  setAspetta(true);
                  if (v.pronti[io]) stanzaGruppo.pronto(false);
                }
              }}
            >
              {aspetta ? t("gioco.pronto") : t("gioco.aspetta_attimo")}
            </button>
          ) : (
            <button
              type="button"
              className={`pulsante pulsante-primario pulsante-grande ${v.pronti[io] ? "pulsante-fatto" : ""}`}
              aria-pressed={v.pronti[io]}
              onClick={() => {
                sbloccaAudio();
                stanzaGruppo.pronto(!v.pronti[io]);
              }}
            >
              {v.pronti[io] ? t("gioco.pronto_fatto") : t("gruppo.ancora")}
            </button>
          )}
          {v.capo === io ? (
            // v040: «Via!» si vede solo quando si può premere, grande e in evidenza, con quanti siete (si può partire
            // anche in 3 senza aspettare il quarto); prima era un pulsante grigio sotto «Aspetta un attimo»
            abbastanza && tuttiPronti ? (
              <>
                <button type="button" className="pulsante pulsante-primario pulsante-grande ld-via-gruppo" onClick={() => stanzaGruppo.avvia()}>
                  {collegati.length < 4 ? t("gruppo.via_in", { n: collegati.length }) : t("gruppo.via")}
                </button>
                <p className="nota-centrata">{collegati.length < 4 && v.fase === "attesa" ? t("gruppo.via_o_aspetta") : t("gruppo.puoi_dare_via")}</p>
              </>
            ) : (
              <p className="nota-centrata">{!abbastanza ? t("gruppo.servono", { n: v.minimo }) : t("gruppo.aspetta_pronti")}</p>
            )
          ) : (
            <p className="nota-centrata">{t("gruppo.aspetta_via", { nome: v.capo ? nome(v.capo) : "?" })}</p>
          )}
        </div>
      );
    }
    case "conto":
      // v042: lo stesso conto alla rovescia grande dell'1 contro 1 (prima qui i numeri erano piccoli)
      return (
        <div className="conto ld-conto-grande" role="timer">
          <span key={numeroConto} className="conto-numero" data-n={numeroConto}>
            {numeroConto > 0 ? numeroConto : t("gioco.via")}
          </span>
          <span className="ld-conto-nota">{t("gioco.non_ridere")}</span>
        </div>
      );
    case "gioco":
      return <p className="pannello-testo">{inGara(v, io) ? t("gruppo.gioco_istruzioni") : t("gruppo.sei_fuori")}</p>;
    case "verifica":
      return <p className="pannello-titolo lampo">{t("gioco.risata")}</p>;
    case "accusa": {
      const a = v.accusa!;
      if (a.contro === io) {
        return (
          <div className="pannello-accusa" role="alertdialog" aria-labelledby="accusa-titolo">
            <p id="accusa-titolo" className="pannello-titolo">
              {t("gioco.accusa_tu")}
            </p>
            <p className="pannello-testo">{a.origine === "giocatore" ? t("gioco.accusa_tu_testo", { nome: nome(a.da) }) : t("gioco.accusa_analisi")}</p>
            <div className="azioni-riga">
              <button type="button" className="pulsante pulsante-pericolo" onClick={() => stanzaGruppo.rispondi(true)}>
                {t("gioco.si_ho_riso")}
              </button>
              <button type="button" className="pulsante pulsante-secondario" onClick={() => stanzaGruppo.rispondi(false)}>
                {t("gioco.no_non_ho_riso")} ({secondi(a.scade - ora)})
              </button>
            </div>
          </div>
        );
      }
      return <p className="pannello-titolo">{t("gioco.accusa_altro", { nome: nome(a.contro) })}</p>;
    }
    case "pausa": {
      const pa = v.pausa!;
      const testo = pa.motivo === "scollegato" ? t("gioco.pausa_scollegato", { nome: nome(pa.chi) }) : pa.chi === io ? t("gioco.pausa_volto_io") : t("gioco.pausa_volto", { nome: nome(pa.chi) });
      return (
        <div className="pannello-pausa">
          <p className="pannello-titolo">{testo}</p>
          <p className="nota-centrata">{t("gioco.pausa_scade", { secondi: secondi(pa.scade - ora) })}</p>
        </div>
      );
    }
  }
}
